import { cn } from "utils";
import { LottieLoading, VirtualList } from "@ospweb/sdk-ui";
import { useMemoizedFn } from "ahooks";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { useSnapshot } from "valtio";
import { TAListItem } from "./TAListItem";
import { TAModalCallable } from "./TAModalCallable";
import { TAListProps } from "./types";
import { useTASectionData } from "@/src/hooks/useTASectionData";
import { useLoginModalProvider } from "@/src/hooks/useLoginModalProvider";
import { rootStore } from "@/src/store";
import { AgentReport } from "@/src/features/api/memberApi";
import { I18nUtil } from "@utils/i18n";
import Translations from "@translate/components/TA/TAList";
import { EmptyState } from "@/src/components/Empty";

const I18n = I18nUtil.createScoped(Translations);

export const TAList = (props: TAListProps) => {
  const { type, filterStrongSignalsOnly, filterOption } = props;
  const { isLoggedIn } = useSnapshot(rootStore.login);
  const { open: openLoginModal } = useLoginModalProvider();
  const loadMoreObserverRef = useRef<IntersectionObserver | null>(null);

  const {
    displayData,
    isLoading,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    hasAssetsForCurrentMarket,
    rawApiHasData,
    handleExploreNow,
  } = useTASectionData({
    type,
    filterOption,
    filterStrongSignalsOnly,
  });

  const handleEndReached = useMemoizedFn(async () => {
    if (hasNextPage && !isFetchingNextPage) {
      await fetchNextPage();
    }
  });

  const loadingComponent = useMemo(
    () => (
      <div className="flex flex-col items-center justify-center h-20 pb-4 overflow-hidden min-h-[400px]">
        <LottieLoading className="w-5 h-5" size={20} />
      </div>
    ),
    [],
  );

  useEffect(() => {
    return () => {
      loadMoreObserverRef.current?.disconnect();
      loadMoreObserverRef.current = null;
    };
  }, []);

  const lastItemRef = useCallback(
    (node: HTMLDivElement | null) => {
      loadMoreObserverRef.current?.disconnect();
      if (!node) {
        return;
      }
      loadMoreObserverRef.current = new IntersectionObserver(
        (entries) => {
          const entry = entries[0];
          if (
            entry?.isIntersecting &&
            hasNextPage &&
            !isFetchingNextPage &&
            !isLoading
          ) {
            fetchNextPage();
          }
        },
        {
          root: null,
          rootMargin: "200px 0px",
          threshold: 0.01,
        },
      );
      loadMoreObserverRef.current.observe(node);
    },
    [fetchNextPage, hasNextPage, isFetchingNextPage, isLoading],
  );

  const onItemClick = useMemoizedFn(async (data: AgentReport) => {
    await TAModalCallable({
      data,
    });
  });

  // // 没有更多数据内容
  // const noMoreContent = (
  //   <div className="body_m text-content_quaternary mt-4 justify-self-center">
  //     {I18n.t("no more technical analysis")}
  //   </div>
  // );

  if (isLoading) {
    return loadingComponent;
  }

  const TABLE_HEADERS = [
    { key: "time" as const, labelKey: "Time" },
    { key: "asset" as const, labelKey: "Asset" },
    { key: "price" as const, labelKey: "Price" },
    { key: "taConclusion" as const, labelKey: "TA Conclusion" },
  ] as const;

  if (displayData.length > 0) {
    return (
      <div className="flex flex-col gap-3">
        <div className="flex w-full flex-col overflow-x-auto">
          <div className="flex w-full items-center border-b border-border_white_transparent10">
            {TABLE_HEADERS.map((h) => (
              <div
                key={h.key}
                className={cn(
                  "flex min-w-0 flex-1 items-center px-2 py-3",
                  h.key === "taConclusion" && "justify-center",
                )}
              >
                <p className="body_medium_s text-content_secondary whitespace-nowrap">
                  {I18n.t(h.labelKey)}
                </p>
              </div>
            ))}
          </div>
          <VirtualList
            onEndReachedThreshold="100"
            noMoreContainerClassName="h-20"
            hasNextPage={hasNextPage}
            onEndReached={handleEndReached}
            renderLoading={loadingComponent}
          >
            <div className="flex w-full flex-col">
              {displayData.map((taData, index) => {
                const isLastItem = index === displayData.length - 1;
                return (
                  <div
                    key={taData.id}
                    ref={isLastItem ? lastItemRef : undefined}
                  >
                    <TAListItem
                      data-openreplay-label="TA · TAList · TAListItem"
                      data={taData}
                      onClick={onItemClick}
                    />
                  </div>
                );
              })}
            </div>
          </VirtualList>
        </div>
      </div>
    );
  }

  // Watchlist 未登录：展示去登录按钮，样式与 Explore Now 一致
  if (filterOption === "Watchlist" && !isLoggedIn) {
    return (
      <div className="flex w-full flex-col items-center justify-center gap-3 px-10 py-6 text-center">
        <EmptyState
          className="max-w-[313px]"
          title={I18n.t("Log in to view your watchlist.")}
          description=" "
          titleClassName="title_m text-content_secondary mt-0 mb-1"
          descriptionClassName="body_s text-content_tertiary"
        />
        <button
          aria-label="TAList"
          data-openreplay-label={`TAList Button | ${I18n.t("Log in")}`}
          id="btn-talist"
          type="button"
          onClick={() => openLoginModal?.()}
          className={cn(
            "button_s rounded-[6px] px-2 py-1.5",
            "bg-background_white_overlay100 text-content_inverse_primary",
          )}
        >
          {I18n.t("Log in")}
        </button>
      </div>
    );
  }

  // Watchlist 筛选下：接口原始结果为空时，按当前市场（美股/港股/Crypto）是否有关注展示不同空态
  if (filterOption === "Watchlist" && !rawApiHasData) {
    if (!hasAssetsForCurrentMarket) {
      return (
        <div className="flex w-full flex-col items-center justify-center gap-3 px-10 py-6 text-center">
          <EmptyState
            className="max-w-[313px]"
            title={I18n.t("Your Watchlist is Empty")}
            description={I18n.t(
              "Follow stocks or crypto to get real-time TA signals and Alpha insights.",
            )}
            titleClassName="title_m text-content_secondary mt-0 mb-1"
            descriptionClassName="body_s text-content_tertiary"
          />
          <button
            aria-label="TAList"
            data-openreplay-label={`TAList Button | ${I18n.t("Explore Now")}`}
            id="btn-talist-2"
            type="button"
            onClick={handleExploreNow}
            className={cn(
              "button_s rounded-[6px] px-2 py-1.5",
              "bg-background_white_overlay100 text-content_inverse_primary",
            )}
          >
            {I18n.t("Explore Now")}
          </button>
        </div>
      );
    }
  }

  return (
    <div className="w-full h-full overflow-y-auto no-scrollbar flex pt-[121px] justify-center min-h-[436px]">
      <span className="body_m text-content_quaternary">
        {I18n.t("No active signals")}
      </span>
    </div>
  );
};
